import {GitHubAvatar} from '@github-ui/github-avatar'
import {XIcon} from '@primer/octicons-react'
import {Heading, IconButton} from '@primer/react'

import {anchorComment} from '../../helpers/anchor-helpers'
import type {AddCommentEditorProps, CommentLineChangeConfig} from './AddCommentEditor'
import {AddCommentEditor} from './AddCommentEditor'
import {ChangeCommentLineNumberButton} from './ChangeCommentLineNumberButton'
import {useState, useId} from 'react'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useDialogModeActions} from '../../stores/selectors'

const COMMENT_POSITIONING_ID_PREFIX = 'diff-comment-positioning-'

export interface StartThreadCommentProps extends Pick<
  AddCommentEditorProps,
  | 'batchingEnabled'
  | 'fileLevelComment'
  | 'filePath'
  | 'focusOnMount'
  | 'lineNumber'
  | 'startLineNumber'
  | 'suggestedChangesConfig'
> {
  addCommentDialogTitle?: string
  isLeftSide: boolean | undefined
  onAddComment: (args: {
    onCompleted?: (threadId: string, commentDatabaseId: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean | undefined
    text: string
  }) => void
  onClose?: (shouldFocusCell?: boolean) => void
  viewerData: {
    avatarUrl: string
    login: string
  }
  showOnCloseIcon?: boolean
  threadsConnectionId?: string
  setStartLineNumber?: (lineNumber: number | undefined) => void
}

/**
 * The StartThreadComment component is used to render the comment editor when starting a new inline thread.
 */
export function StartThreadComment({
  addCommentDialogTitle,
  isLeftSide,
  fileLevelComment,
  filePath,
  focusOnMount = true,
  lineNumber,
  onAddComment,
  onClose,
  viewerData,
  threadsConnectionId,
  showOnCloseIcon = true,
  ...rest
}: StartThreadCommentProps) {
  const [commentLineChangeConfig, setCommentLineChangeConfig] = useState<CommentLineChangeConfig | undefined>(undefined)
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const moveNewCommentFlagEnabled = useFeatureFlag('move_new_comments_ui')
  // This feature is not current enabled
  // Issue: https://github.com/github/pull-requests/issues/19502
  const moveNewCommentEnabled = moveNewCommentFlagEnabled && false
  const {disableDialogMode} = useDialogModeActions()
  const handleAddComment = ({
    commentText,
    onCompleted,
    onError,
    submitBatch,
  }: {
    commentText: string
    onCompleted?: (threadId: string, commentDatabaseId?: number | undefined | null) => void
    onError: (error: Error) => void
    submitBatch?: boolean
  }) => {
    const handleCompleted = (threadId: string, commentDatabaseId: number | undefined | null) => {
      if (commentDatabaseId) anchorComment(commentDatabaseId.toString())
      onCompleted?.(threadId, commentDatabaseId)
    }

    onAddComment({
      text: commentText,
      onError,
      onCompleted: handleCompleted,
      submitBatch,
    })
  }

  const onCommentClose = () => {
    onClose?.()
    disableDialogMode()
  }
  const commentPositioningId = `${COMMENT_POSITIONING_ID_PREFIX}${useId()}`

  return (
    <div className="px-2 pb-2 pt-1">
      <div className="d-flex flex-justify-between flex-items-center">
        <Heading as="h4" className="f5 ml-1 pt-1 pb-2 d-flex">
          <GitHubAvatar alt={viewerData.login} size={24} src={viewerData.avatarUrl || ''} className="mr-2" />

          {!moveNewCommentEnabled &&
            (fileLevelComment ? (
              <>Add comment on file</>
            ) : (
              <span>{addCommentDialogTitle ? `Add a comment on ${addCommentDialogTitle}` : 'Add a comment'}</span>
            ))}
          {moveNewCommentEnabled && (
            <div className="d-flex flex-items-center" id={commentPositioningId}>
              <div className="pt-1">Add {fileLevelComment ? '' : 'a'} comment on </div>
              <ChangeCommentLineNumberButton
                ariaLabelId={commentPositioningId}
                currentLineNumberText={fileLevelComment ? 'file' : (addCommentDialogTitle ?? '')}
                baseEndLineNumber={lineNumber}
                baseStartLineNumber={rest.startLineNumber}
                diffSide={isLeftSide ? 'LEFT' : 'RIGHT'}
                setCommentLineChangeConfig={setCommentLineChangeConfig}
              />
            </div>
          )}
        </Heading>
        {onClose && showOnCloseIcon && (
          <IconButton variant="invisible" icon={XIcon} onClick={onCommentClose} aria-label="Cancel" />
        )}
      </div>
      <AddCommentEditor
        focusOnMount={focusOnMount}
        condensed={false}
        fileLevelComment={fileLevelComment ?? false}
        filePath={filePath}
        lineNumber={lineNumber}
        onAddComment={handleAddComment}
        onCancelComment={onCommentClose}
        commentLineChangeConfig={commentLineChangeConfig}
        side={isLeftSide ? 'LEFT' : 'RIGHT'}
        {...rest}
      />
    </div>
  )
}
